import i18next from "i18next";
import styles from "../modals/mark-modals.styles.css";
import { detachImageConnection } from "./event-handlers";
import { getHostWindowId, getStoreByControlId } from "./image-tools";
import { runInAction } from "mobx";

export function showMarkControlContextMenu(e: MouseEvent, controlId: string) {
  e.preventDefault();
  document.querySelectorAll(".mark-context-menu").forEach(el => el.remove());
  const control =
  (e.target as HTMLElement)?.closest("ms-markcontrol") as HTMLElement
  || (document.querySelector("ms-markcontrol.opened") as HTMLElement | null)
  || (document.getElementById(controlId) as HTMLElement | null);

if (!control) return;
  const existingStyle = document.getElementById("mark-context-style");

  if (!existingStyle) {
    const styleTag = document.createElement("style");
    styleTag.id = "mark-context-style";
    styleTag.textContent = styles;
    document.head.appendChild(styleTag);
  }

  const menu = document.createElement("div");
  menu.className = "mark-context-menu";
  Object.assign(menu.style, {
    position: "absolute",
    left: `${e.clientX}px`,
    top: `${e.clientY}px`,
  });

  const makeOption = (
    label: string,
    onClick: () => void,
    enabled: boolean = true
  ) => {
    const item = document.createElement("div");
    item.className = "mark-context-menu-item";
    item.textContent = label;

    if (!enabled) {
      item.classList.add("disabled");
    } else {
      item.onclick = () => {
        onClick();
        menu.remove();
      };
    }
    return item;
  };
  
  const cleanLabel = control.textContent.replace("👁️","");
  const store = getStoreByControlId(controlId);
  const hostWindowId = getHostWindowId(control);
  const anyImages = !!hostWindowId && document.querySelectorAll(`.image-container[data-control-id="${controlId}"][data-window-id="${hostWindowId}"] img.dropped-image`).length > 0;
  const anyHidden = !!hostWindowId && store.droppedImages.some(img => img.windowId === hostWindowId && img.hidden === true);
  menu.appendChild(
    makeOption(`${i18next.t("delete_all")} ${cleanLabel.toLowerCase()}`, () => {
      if ($pm.hasPermissions && !($pm.checkAllow($pm.getEntryFromElement(control,"ManageMarksAndPosters")))) return;
      const controlId = control.getAttribute("id");
      const hostWindowId = getHostWindowId(control);
      const store = getStoreByControlId(controlId);
      if (!store || !hostWindowId) return;
      runInAction(() => {
        store.droppedImages.forEach(img => {
          if (img.windowId === hostWindowId && img.linkedElementId) {
            detachImageConnection(img);
          }
        });
        store.droppedImages = store.droppedImages.filter(img => img.windowId !== hostWindowId);
      });

      const win = control.closest("ms-window") as HTMLElement | null;
      if (win) {
        win.querySelectorAll(`.image-container[data-control-id="${controlId}"][data-window-id="${hostWindowId}"]`).forEach(el => el.remove());
      } else {
        document.querySelectorAll(`.image-container[data-control-id="${controlId}"][data-window-id="${hostWindowId}"]`).forEach(el => el.remove());
      }
      showHideEyeIndicatorForControl(control);
    }, anyImages)
  );

  if (anyHidden) {
    menu.appendChild(
      makeOption(`${i18next.t("show_all")} ${cleanLabel.toLowerCase()}`, () => {
        const controlId = control.getAttribute("id");
        const hostWindowId = getHostWindowId(control);
        const store = getStoreByControlId(controlId);
        if (!store || !hostWindowId) return;
        runInAction(() => {
          store.droppedImages.forEach(img => {
            if (img.windowId === hostWindowId) img.hidden = false;
          });
        });
        const win = control.closest("ms-window") as HTMLElement | null;
        const root: ParentNode = win ?? document;
        root.querySelectorAll(`.dropped-image[data-control-id="${controlId}"][data-window-id="${hostWindowId}"]`).forEach(el => ((el as HTMLElement).style.visibility = "visible"));
        showHideEyeIndicatorForControl(control);
      })
    )
  } else {
    menu.appendChild(
      makeOption(`${i18next.t("hide_all")} ${cleanLabel.toLowerCase()}`, () => {
        const controlId = control.getAttribute("id");
        const hostWindowId = getHostWindowId(control);
        const store = getStoreByControlId(controlId);
        if (!store || !hostWindowId) return;

        runInAction(() => {
          store.droppedImages.forEach(img => {
            if (img.windowId === hostWindowId) img.hidden = true;
          });
        });
        const win = control.closest("ms-window") as HTMLElement | null;
        const root: ParentNode = win ?? document;
        root.querySelectorAll(`.dropped-image[data-control-id="${controlId}"][data-window-id="${hostWindowId}"]`).forEach(el => ((el as HTMLElement).style.visibility = "hidden"));
        showHideEyeIndicatorForControl(control);
      }, anyImages))
    }

    document.body.appendChild(menu);
    const closeOnClickOutside = (event: MouseEvent) => {
      if (!menu.contains(event.target as Node)) {
        menu.remove();
        document.removeEventListener("mousedown", closeOnClickOutside);
      }
    };
    document.addEventListener("mousedown", closeOnClickOutside);
}

export function showHideEyeIndicator(controlId: string) {
  const control = document.getElementById(controlId);
  if (!control) return;

  const store = getStoreByControlId(controlId);
  if (!store) return;
  const anyHidden = store.droppedImages.some(img => img.hidden === true);
  let indicator = control.querySelector(".mark-hidden-indicator") as HTMLElement;

  if (anyHidden) {
    if (!indicator) {
      indicator = document.createElement("span");
      indicator.className = "mark-hidden-indicator";
      indicator.textContent = "👁️";
      indicator.style.marginLeft = "5px";
      Object.assign(indicator.style, {
        position: "absolute",
        top: "1%",
        left: "10%",
        transform: "translateX(-50%)",
        fontSize: "16px",
        display: "block",
        zIndex: "1000",
      });
      control.appendChild(indicator);
    }
  } else {
    if (indicator) indicator.remove();
  }
}

export function showHideEyeIndicatorForControl(control: HTMLElement) {
  const controlId = control.getAttribute("id");
  if (!controlId) return;

  const hostWindowId = getHostWindowId(control);
  const store = getStoreByControlId(controlId);
  if (!store || !hostWindowId) return;

  const anyHidden = store.droppedImages.some(img => img.windowId === hostWindowId && img.hidden === true);

  let indicator = control.querySelector(".mark-hidden-indicator") as HTMLElement;
  if (anyHidden) {
    if (!indicator) {
      indicator = document.createElement("span");
      indicator.className = "mark-hidden-indicator";
      indicator.textContent = "👁️";
      indicator.style.marginLeft = "5px";
      Object.assign(indicator.style, {
        position: "absolute",
        top: "1%",
        left: "10%",
        transform: "translateX(-50%)",
        fontSize: "16px",
        display: "block",
        zIndex: "1000",
      });
      control.appendChild(indicator);
    }
  } else {
    indicator?.remove();
  }
}